import { useEffect, lazy, Suspense, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useLocation, Navigate, useOutlet } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { ScrollToTop } from "./components/ScrollToTop";
import { AuthProvider } from "./hooks/useAuth";
import { ProtectedRoute } from "./components/ProtectedRoute";
import { trackPageView } from "./lib/analytics";
import { supabase } from "@/integrations/supabase/client";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { ScrollDepthTracker as ScrollDepthTrackerComponent } from "./components/ScrollDepthTracker";
import { initAssetMonitor } from "./lib/assetMonitor";
import { hasConsented } from "./lib/cookieConsent";
import CookieConsentBanner from "./components/CookieConsentBanner";
import Index from "./pages/Index";

// Lazy-load non-critical routes to reduce initial bundle size
const Insights = lazy(() => import("./pages/Insights"));
const BlogPost = lazy(() => import("./pages/BlogPost"));
const TechStack = lazy(() => import("./pages/TechStack"));
const Schedule = lazy(() => import("./pages/Schedule"));
const NotFound = lazy(() => import("./pages/NotFound"));
const AdminLogin = lazy(() => import("./pages/AdminLogin"));
const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));
const AdminNewPost = lazy(() => import("./pages/AdminNewPost"));
const AdminEditPost = lazy(() => import("./pages/AdminEditPost"));
const EgenNewCampaign = lazy(() => import("./pages/egen/EgenNewCampaign"));
const EgenCampaignDetail = lazy(() => import("./pages/egen/EgenCampaignDetail"));
const IngenNewProject = lazy(() => import("./pages/ingen/IngenNewProject"));
const IngenProjectDetail = lazy(() => import("./pages/ingen/IngenProjectDetail"));
const IngenAssetReview = lazy(() => import("./pages/ingen/IngenAssetReview"));
const IngenRepostHub = lazy(() => import("./pages/ingen/IngenRepostHub"));
const IngenCalendar = lazy(() => import("./pages/ingen/IngenCalendar"));
const PreviewEmbed = lazy(() => import("./pages/PreviewEmbed"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const ShortRedirect = lazy(() => import("./pages/ShortRedirect"));
const GeneralRedirect = lazy(() => import("./pages/GeneralRedirect"));
const StagingHome = lazy(() => import("./pages/staging/StagingHome"));
const Unsubscribe = lazy(() => import("./pages/Unsubscribe"));
const Roadmap = lazy(() => import("./pages/Roadmap"));
const StagingAdopt = lazy(() => import("./pages/staging/StagingAdopt"));
const StagingBuild = lazy(() => import("./pages/staging/StagingBuild"));
const StagingCaseStudies = lazy(() => import("./pages/staging/StagingCaseStudies"));
const StagingCaseStudyDetail = lazy(() => import("./pages/staging/StagingCaseStudyDetail"));
const StagingEnable = lazy(() => import("./pages/staging/StagingEnable"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

function PageViewTracker() {
  const location = useLocation();
  useEffect(() => {
    if (!location.pathname.startsWith("/admin") && hasConsented()) {
      trackPageView(location.pathname);
    }
  }, [location.pathname]);
  return null;
}

function AssetMonitorInit() {
  useEffect(() => {
    if (!window.location.pathname.startsWith("/admin")) {
      initAssetMonitor();
    }
  }, []);
  return null;
}

function GoogleAnalyticsLoader() {
  const location = useLocation();
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (location.pathname.startsWith("/admin")) return;

    const tryLoad = () => {
      if (loaded || !hasConsented()) return;
      setLoaded(true);
      loadGA();
    };

    // Check immediately
    tryLoad();

    // Also listen for mid-session consent
    const handler = () => tryLoad();
    window.addEventListener("consent-granted", handler);
    return () => window.removeEventListener("consent-granted", handler);
  }, [loaded, location.pathname]);

  return null;
}

async function loadGA() {
  const { data } = await (supabase as any)
    .from("public_site_settings")
    .select("ga_measurement_id")
    .limit(1)
    .single();

  const id = (data as any)?.ga_measurement_id;
  if (!id || typeof id !== "string" || !id.startsWith("G-")) return;

  const scriptTag = document.createElement("script");
  scriptTag.async = true;
  scriptTag.src = `https://www.googletagmanager.com/gtag/js?id=${id}`;
  document.head.appendChild(scriptTag);

  const inlineTag = document.createElement("script");
  inlineTag.textContent = `window.dataLayer=window.dataLayer||[];function gtag(){dataLayer.push(arguments);}gtag('js',new Date());gtag('config','${id}',{anonymize_ip:true});`;
  document.head.appendChild(inlineTag);
}

function GoogleTagManagerLoader() {
  const location = useLocation();
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (location.pathname.startsWith("/admin")) return;

    const tryLoad = () => {
      if (loaded || !hasConsented()) return;
      setLoaded(true);
      loadGTM();
    };

    tryLoad();

    const handler = () => tryLoad();
    window.addEventListener("consent-granted", handler);
    return () => window.removeEventListener("consent-granted", handler);
  }, [loaded, location.pathname]);

  return null;
}

async function loadGTM() {
  const { data } = await (supabase as any)
    .from("public_site_settings")
    .select("gtm_container_id")
    .limit(1)
    .single();

  const id = (data as any)?.gtm_container_id;
  if (!id || typeof id !== "string" || !id.startsWith("GTM-")) return;

  const scriptTag = document.createElement("script");
  scriptTag.textContent = `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);})(window,document,'script','dataLayer','${id}');`;
  document.head.appendChild(scriptTag);

  const noscriptContainer = document.createElement("div");
  noscriptContainer.innerHTML = '<noscript><iframe src="https://www.googletagmanager.com/ns.html?id=' + id + '" height="0" width="0" style="display:none;visibility:hidden"></iframe></noscript>';
  document.body.insertBefore(noscriptContainer, document.body.firstChild);
}

function AnimatedRoutes() {
  const location = useLocation();

  return (
    <AnimatePresence mode="popLayout" initial={false}>
      <motion.div
        key={location.pathname}
        initial={{ opacity: 1 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.12, ease: "easeOut" }}
      >
        <Suspense fallback={<div className="min-h-screen flex items-center justify-center"><div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" /></div>}>
          <Routes location={location}>
            {/* ── PUBLIC / PRODUCTION ────────────────────────────────── */}
            <Route path="/" element={<Index />} />
            <Route path="/insights" element={<Insights />} />
            <Route path="/insights/:slug" element={<BlogPost />} />
            <Route path="/tech-stack" element={<TechStack />} />
            <Route path="/schedule" element={<Schedule />} />
            <Route path="/roadmap" element={<Roadmap />} />
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/unsubscribe" element={<Unsubscribe />} />

            {/* ── STAGING (UNLISTED — public by URL, noindex) ─────────── */}
            {/* Reachable by anyone with the link so they can be shared
                with prospects, but kept out of search via per-page
                <meta name="robots" content="noindex, nofollow"> AND a
                Disallow: /staging/ entry in public/robots.txt. Not linked
                from production nav, footer, sitemap, or homepage. The
                <StagingBanner /> component shows a loud amber bar to
                admins and a subtle "Preview link" chip to everyone else. */}
            <Route path="/staging" element={<StagingHome />} />
            <Route path="/staging/adopt" element={<StagingAdopt />} />
            <Route path="/staging/build" element={<StagingBuild />} />
            <Route path="/staging/enable" element={<StagingEnable />} />
            <Route path="/staging/case-studies" element={<StagingCaseStudies />} />
            <Route path="/staging/case-studies/:slug" element={<StagingCaseStudyDetail />} />

            {/* ── ADMIN ──────────────────────────────────────────────── */}
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
            <Route path="/admin/posts" element={<Navigate to="/admin?tab=posts" replace />} />
            <Route path="/admin/case-studies" element={<Navigate to="/admin?tab=homepage" replace />} />
            <Route path="/admin/new-post" element={<ProtectedRoute><AdminNewPost /></ProtectedRoute>} />
            <Route path="/admin/edit/:id" element={<ProtectedRoute><AdminEditPost /></ProtectedRoute>} />
            <Route path="/admin/egen" element={<Navigate to="/admin?tab=marketing&subtab=egen" replace />} />
            <Route path="/admin/egen/new" element={<ProtectedRoute><EgenNewCampaign /></ProtectedRoute>} />
            <Route path="/admin/egen/campaign/:id" element={<ProtectedRoute><EgenCampaignDetail /></ProtectedRoute>} />
            <Route path="/admin/ingen" element={<Navigate to="/admin?tab=marketing&subtab=ingen" replace />} />
            <Route path="/admin/ingen/new" element={<ProtectedRoute><IngenNewProject /></ProtectedRoute>} />
            <Route path="/admin/ingen/project/:id" element={<Navigate to="assets" replace />} />
            <Route path="/admin/ingen/project/:id/assets" element={<ProtectedRoute><IngenAssetReview /></ProtectedRoute>} />
            <Route path="/admin/ingen/project/:id/repost" element={<ProtectedRoute><IngenRepostHub /></ProtectedRoute>} />
            <Route path="/admin/ingen/calendar" element={<ProtectedRoute><IngenCalendar /></ProtectedRoute>} />

            {/* ── INFRASTRUCTURE / EMBEDS ────────────────────────────── */}
            <Route path="/s/:code" element={<ShortRedirect />} />
            <Route path="/r/*" element={<GeneralRedirect />} />
            <Route path="/booking/*" element={<GeneralRedirect />} />
            <Route path="/preview-embed" element={<PreviewEmbed />} />

            {/* ── LEGACY REDIRECTS ───────────────────────────────────── */}
            {/* Preserve old shared links (LinkedIn campaigns, prior
                staging shares, internal docs) so nothing 404s. */}
            <Route path="/staging/dev-teams" element={<Navigate to="/staging/build" replace />} />
            <Route path="/staging/solutions" element={<Navigate to="/staging/adopt" replace />} />
            <Route path="/staging/insights" element={<Navigate to="/insights" replace />} />
            <Route path="/staging/schedule" element={<Navigate to="/schedule" replace />} />
            <Route path="/staging/roadmap" element={<Navigate to="/roadmap" replace />} />
            <Route path="/roadmap-staging" element={<Navigate to="/roadmap" replace />} />

            {/* ── CATCH-ALL ──────────────────────────────────────────── */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Suspense>
      </motion.div>
    </AnimatePresence>
  );
}

const App = () => (
  <HelmetProvider>
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <TooltipProvider>
          <Toaster />
          <Sonner />
          <BrowserRouter>
            <ScrollToTop />
            <PageViewTracker />
            <ScrollDepthTrackerComponent />
            <AssetMonitorInit />
            <GoogleAnalyticsLoader />
            <GoogleTagManagerLoader />
            <ErrorBoundary>
              <AnimatedRoutes />
            </ErrorBoundary>
            <CookieConsentBanner />
          </BrowserRouter>
        </TooltipProvider>
      </AuthProvider>
    </QueryClientProvider>
  </HelmetProvider>
);

export default App;
